import { useMemo, useState } from "react";
import {
  Archive,
  Ban,
  Filter,
  Folder,
  Inbox,
  LayoutDashboard,
  PenLine,
  Plus,
  Send,
  Sparkles,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import type { FilterColor, FolderDto, FolderKind } from "../../../shared/types";
import type { ActiveTab } from "../../App";
import {
  useAccounts,
  useAssignToVirtualFolder,
  useFilters,
  useFolders,
  useInfiniteMessages,
  useLabels,
  useSenderGroups,
  useVirtualFolders,
} from "../../hooks/useApi";
import { OTHER_FILTER_ID, SWATCH_CLASSES } from "../filterColors";
import { MAIL_DRAG_TYPE, parseMessageDrop, type MessageDragPayload } from "../mail/dragMessage";

const KIND_ICONS: Record<FolderKind, LucideIcon> = {
  inbox: Inbox,
  drafts: PenLine,
  sent: Send,
  deleted: Trash2,
  junk: Ban,
  archive: Archive,
  custom: Folder,
};

const KIND_ORDER: FolderKind[] = ["inbox", "drafts", "sent", "deleted", "junk", "archive"];

function sortFolders(folders: FolderDto[]): FolderDto[] {
  return [...folders].sort((a, b) => {
    const ai = a.kind === "custom" ? KIND_ORDER.length : KIND_ORDER.indexOf(a.kind);
    const bi = b.kind === "custom" ? KIND_ORDER.length : KIND_ORDER.indexOf(b.kind);
    if (ai !== bi) return ai - bi;
    return a.name.localeCompare(b.name);
  });
}

function NavItem({
  label,
  icon: Icon,
  dotColor,
  dotShape = "circle",
  active,
  count,
  onClick,
  onDropMessage,
}: {
  label: string;
  icon?: LucideIcon;
  /** Render a colored swatch (filter color) instead of a lucide icon. */
  dotColor?: FilterColor;
  /** circle = AI groups; rect = sender filters. */
  dotShape?: "circle" | "rect";
  active?: boolean;
  count?: number;
  onClick?: () => void;
  /** Accept dragged mail rows (virtual folders). */
  onDropMessage?: (payload: MessageDragPayload) => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  const dropHandlers = onDropMessage
    ? {
        // Data is unreadable during dragover — gate on the MIME type only.
        onDragOver: (e: React.DragEvent) => {
          if (!e.dataTransfer.types.includes(MAIL_DRAG_TYPE)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
        },
        onDragEnter: (e: React.DragEvent) => {
          if (e.dataTransfer.types.includes(MAIL_DRAG_TYPE)) setDragOver(true);
        },
        onDragLeave: () => setDragOver(false),
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          setDragOver(false);
          const payload = parseMessageDrop(e);
          if (payload) onDropMessage(payload);
        },
      }
    : {};
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      {...dropHandlers}
      className={`flex w-full items-center gap-2.5 rounded px-3 py-[3px] text-left text-[13px] ${
        active ? "bg-accent-subtle font-semibold text-accent" : "text-ink hover:bg-black/5"
      } ${dragOver ? "bg-accent-subtle ring-1 ring-accent" : ""}`}
    >
      {dotColor ? (
        <span className="grid w-[15px] shrink-0 place-items-center">
          <span
            className={`${
              dotShape === "rect" ? "h-2.5 w-3.5 rounded-[3px]" : "size-2.5 rounded-full"
            } ${SWATCH_CLASSES[dotColor]}`}
          />
        </span>
      ) : (
        Icon && <Icon size={15} strokeWidth={2} className="shrink-0 text-ink-muted" />
      )}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined && count > 0 && (
        <span className="shrink-0 text-[12px] font-semibold text-accent">{count}</span>
      )}
    </button>
  );
}

export function NavPane({
  activeTab,
  selectedFolder,
  onSelectFolder,
  selectedFilter,
  onSelectFilter,
  onRunFilters,
  selectedSenderFilter,
  onSelectSenderFilter,
  onCreateGroup,
  selectedVirtualFolder,
  onSelectVirtualFolder,
  onCreateVirtualFolder,
  hiddenAccountIds,
  onToggleAccount,
}: {
  activeTab: ActiveTab;
  selectedFolder: string | null;
  onSelectFolder: (folderId: string | null) => void;
  selectedFilter: number | null;
  onSelectFilter: (filterId: number | null) => void;
  onRunFilters: () => void;
  selectedSenderFilter: number | null;
  onSelectSenderFilter: (filterId: number | null) => void;
  onCreateGroup: () => void;
  selectedVirtualFolder: number | null;
  onSelectVirtualFolder: (folderId: number | null) => void;
  onCreateVirtualFolder: () => void;
  hiddenAccountIds: Set<number>;
  onToggleAccount: (accountId: number) => void;
}) {
  // Filter list + folders: subscribe to the same queries MailView drives
  // (shared cache), disabled on the Dashboard tab.
  const onMailTab = activeTab !== null;
  const chipAccount = activeTab ?? -1;
  const { data: folders, isLoading } = useFolders(chipAccount, onMailTab);
  const { data: filters } = useFilters();
  const { data: accounts } = useAccounts();
  const { data: labels } = useLabels(chipAccount, onMailTab);
  const { data: messagesData } = useInfiniteMessages(chipAccount, selectedFolder, onMailTab);
  const { data: senderGroups } = useSenderGroups();
  const { data: virtualFolders } = useVirtualFolders(onMailTab);
  const assignToFolder = useAssignToVirtualFolder();

  // Per-filter counts over the loaded messages, plus how many carry no label.
  const { filterCounts, otherCount } = useMemo(() => {
    const counts = new Map<number, number>();
    let other = 0;
    for (const page of messagesData?.pages ?? []) {
      for (const m of page.messages) {
        const ids = labels?.[m.id] ?? [];
        if (ids.length === 0) other += 1;
        for (const id of ids) counts.set(id, (counts.get(id) ?? 0) + 1);
      }
    }
    return { filterCounts: counts, otherCount: other };
  }, [labels, messagesData]);

  const enabledFilters = (filters ?? [])
    .filter((f) => f.enabled)
    .sort((a, b) => a.order - b.order);

  return (
    <nav className="flex w-[210px] shrink-0 flex-col gap-px overflow-y-auto border-r border-stroke bg-surface p-2">
      {onMailTab && enabledFilters.length > 0 && (
        <>
          <p className="px-3 pb-0.5 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
            AI Group
          </p>
          {/* "All" row with the Run-filters (AI) action right-aligned. Kept as
              two sibling buttons — a button can't nest inside another button. */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => onSelectFilter(null)}
              className={`flex flex-1 items-center gap-2.5 rounded px-3 py-[3px] text-left text-[13px] ${
                selectedFilter === null
                  ? "bg-accent-subtle font-semibold text-accent"
                  : "text-ink hover:bg-black/5"
              }`}
            >
              <Filter size={15} strokeWidth={2} className="shrink-0 text-ink-muted" />
              <span className="min-w-0 flex-1 truncate">All</span>
            </button>
            <button
              onClick={onRunFilters}
              title="Run mail filters (AI)"
              aria-label="Run mail filters with AI"
              className="grid size-7 shrink-0 place-items-center rounded text-accent hover:bg-accent-subtle"
            >
              <Sparkles size={15} strokeWidth={2} />
            </button>
          </div>
          {enabledFilters.map((f) => (
            <NavItem
              key={f.id}
              label={f.name}
              dotColor={f.color}
              count={filterCounts.get(f.id) ?? 0}
              active={selectedFilter === f.id}
              onClick={() => onSelectFilter(f.id)}
            />
          ))}
          <NavItem
            label="Other"
            dotColor="gray"
            count={otherCount}
            active={selectedFilter === OTHER_FILTER_ID}
            onClick={() => onSelectFilter(OTHER_FILTER_ID)}
          />

          {/* Combined "All accounts" view: number chips to toggle accounts on/off. */}
          {activeTab === 0 && (
            <div className="flex flex-wrap gap-1.5 px-3 py-2">
              {(accounts ?? []).map((a, index) => {
                const hidden = hiddenAccountIds.has(a.id);
                return (
                  <span key={a.id} className="group relative">
                    <button
                      onClick={() => onToggleAccount(a.id)}
                      aria-label={`${hidden ? "Show" : "Hide"} emails from ${a.username}`}
                      style={{ backgroundColor: a.color }}
                      className={`grid size-5 place-items-center rounded-full text-[11px] font-bold text-ink ${
                        hidden ? "opacity-35" : ""
                      }`}
                    >
                      {index + 1}
                    </button>
                    <span className="pointer-events-none absolute left-0 top-full z-30 mt-1.5 hidden whitespace-nowrap rounded bg-ink px-2 py-1 text-[11px] text-white shadow-md group-hover:block">
                      {a.username}
                      <span className="block text-white/70">
                        {hidden ? "hidden — click to show" : "click to hide"}
                      </span>
                    </span>
                  </span>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Sender-based filters, grouped. Above Folders. Heading has a right-aligned "+". */}
      {onMailTab && (
        <>
          <div className="flex items-center gap-1 pb-0.5 pt-1.5">
            <p className="flex-1 px-3 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
              Filters
            </p>
            <button
              onClick={onCreateGroup}
              title="New filter group"
              aria-label="New filter group"
              className="grid size-6 shrink-0 place-items-center rounded text-ink-muted hover:bg-black/5 hover:text-accent"
            >
              <Plus size={15} strokeWidth={2} />
            </button>
          </div>
          {(senderGroups ?? []).length === 0 && (
            <p className="px-3 pb-1 text-[11px] text-ink-muted">
              No filters yet — add a group with ＋, then a filter from any email.
            </p>
          )}
          {(senderGroups ?? []).map((group) => (
            <div key={group.id} className="pb-0.5">
              <p className="px-3 pt-1 text-[10px] font-medium uppercase tracking-wide text-ink-muted/70">
                {group.name}
              </p>
              {group.filters.length === 0 ? (
                <p className="px-3 py-1 text-[11px] text-ink-muted/70">
                  Empty — add from an email’s filter icon.
                </p>
              ) : (
                group.filters.map((f) => (
                  <NavItem
                    key={f.id}
                    label={f.name}
                    dotColor={f.color}
                    dotShape="rect"
                    active={selectedSenderFilter === f.id}
                    onClick={() => onSelectSenderFilter(f.id)}
                  />
                ))
              )}
            </div>
          ))}
        </>
      )}

      <div className="flex items-center gap-1 pb-0.5 pt-1.5">
        <p className="flex-1 px-3 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
          {activeTab === null ? "Overview" : "Folders"}
        </p>
        {onMailTab && (
          <button
            onClick={onCreateVirtualFolder}
            title="New folder (stored in this app only)"
            aria-label="New folder"
            className="grid size-6 shrink-0 place-items-center rounded text-ink-muted hover:bg-black/5 hover:text-accent"
          >
            <Plus size={15} strokeWidth={2} />
          </button>
        )}
      </div>

      {activeTab === null && <NavItem label="Dashboard" icon={LayoutDashboard} active />}

      {activeTab !== null && (
        <>
          {isLoading && <p className="px-3 py-1.5 text-[12px] text-ink-muted">Loading folders…</p>}
          {sortFolders(folders ?? []).map((folder) => {
            const isActive =
              folder.kind === "inbox"
                ? selectedFolder === null && selectedVirtualFolder === null
                : selectedFolder === folder.id;
            return (
              <NavItem
                key={folder.id}
                label={folder.name}
                icon={KIND_ICONS[folder.kind]}
                active={isActive}
                count={folder.unreadCount}
                onClick={() => onSelectFolder(folder.kind === "inbox" ? null : folder.id)}
              />
            );
          })}
          {/* User's virtual folders (DB-only) — drop targets for mail rows. */}
          {(virtualFolders ?? []).map((f) => (
            <NavItem
              key={`vf-${f.id}`}
              label={f.name}
              dotColor={f.color}
              active={selectedVirtualFolder === f.id}
              count={f.unreadCount}
              onClick={() => onSelectVirtualFolder(f.id)}
              onDropMessage={(payload) =>
                assignToFolder.mutate({ folderId: f.id, messages: payload.messages })
              }
            />
          ))}
        </>
      )}
    </nav>
  );
}
